import { captureProfileGuard, profileFetch } from "../profileSession";
import { isNativeApp, readToken, writeToken } from "../platform";
import { saveNativeDownload } from "../nativeDownload";
import type { ProfessionalSubmissionInput, ProfessionalAdditionInput } from "./types";

export const API_ORIGIN = ((import.meta as any).env?.VITE_API_ORIGIN ?? "").replace(/\/+$/, "");

export const API_BASE = API_ORIGIN + "/api";

export const MAX_MESSAGE_LENGTH = 4000;

export const MAX_DISPUTE_IMAGE_BYTES = 10 * 1024 * 1024;

/** Keep API assets reachable when a Capacitor page and the API have different
 * origins; preserve already absolute and local blob/data URLs for their callers. */
export function apiAssetUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(value) || /^(?:blob:|data:)/i.test(value)) return value;
  const path = value.startsWith("/") ? value : `/${value}`;
  return API_ORIGIN ? `${API_ORIGIN}${path}` : path;
}

// Token storage uses native application preferences on mobile and localStorage
// on web; ../platform owns the platform-specific persistence.
export function getToken(): string | null {
  return readToken();
}

export function setToken(t: string | null) {
  return writeToken(t);
}

/** Private files stay pinned to their initiating profile through body/name preparation and native saving.
 * Document owners may additionally pin the selected record and retain their filename/error policy. */
export async function downloadApiFile(
  path: string,
  name: string | ((disposition: string) => string | Promise<string>),
  errorMessage: string,
  options: { assertCurrent?: () => void; serverErrors?: boolean } = {},
): Promise<void> {
  const assertProfile = captureProfileGuard();
  const assertCurrent = () => { assertProfile(); options.assertCurrent?.(); };
  const token = getToken();
  const response = await profileFetch(`${API_BASE}${path}`, {
    credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    if (!options.serverErrors) throw new Error(errorMessage);
    const problem: unknown = await response.json().catch(() => null);
    assertCurrent();
    const message = problem && typeof problem === "object" && "error" in problem && typeof problem.error === "string" ? problem.error : errorMessage;
    const code = problem && typeof problem === "object" && "code" in problem && typeof problem.code === "string" ? problem.code : undefined;
    throw new ApiError(response.status, message, code);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="([^"]+)"/i);
  let fileName = typeof name === "function" ? await name(disposition) : name;
  if (typeof name === "string" && match?.[1]) {
    try { fileName = decodeURIComponent(match[1]); } catch { fileName = match[1]; }
  }
  assertCurrent();
  await saveDownloadedFile(blob, fileName, assertCurrent);
  assertCurrent();
}

/** Private downloads share platform handling. Selected-record callers may guard preparation across navigation.
 * Native callers await filesystem persistence; browser callers dispatch a Blob download. */
export async function saveDownloadedFile(blob: Blob, fileName: string, assertCurrent?: () => void): Promise<void> {
  assertCurrent?.();
  if (isNativeApp()) {
    await saveNativeDownload(blob, fileName, assertCurrent);
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url; anchor.download = fileName;
  document.body.appendChild(anchor); anchor.click(); anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export type RequestBehavior = { rejectUnauthorized?: boolean };

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly code?: string) { super(message); }
}

export async function req<T>(path: string, opts: RequestInit = {}, behavior: RequestBehavior = {}): Promise<T> {
  const token = getToken();
  const res = await profileFetch(`${API_BASE}${path}`, {
    ...opts,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (res.status === 401) {
    if (behavior.rejectUnauthorized) throw new ApiError(401, "Authentication is required.");
    return null as T;
  }
  const data = await res.json();
  if (!res.ok) {
    // Increment 43: friendly messages for rate limiting + blocked content.
    if (res.status === 429) {
      const retry = (data as any)?.retryAfter;
      throw new Error((data as any)?.error || `You're doing that too fast. Please wait${retry ? ` ${retry}s` : " a moment"} and try again.`);
    }
    throw new ApiError(res.status, typeof data?.error === "string" ? data.error : `HTTP ${res.status}`,
      typeof data?.code === "string" ? data.code : undefined);
  }
  return data as T;
}

// Owner/Admin evidence, company media and retained portfolio previews share authenticated
// byte access and actionable HTTP errors. Callers own blob URL cleanup.
export async function privateDocumentBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const token = getToken();
  const response = await profileFetch(`${API_BASE}${path}`, { credentials: "include", signal, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) {
    const problem: unknown = await response.json().catch(() => null);
    const message = problem && typeof problem === "object" && "error" in problem && typeof problem.error === "string" ? problem.error : "Could not open this private document. Please retry.";
    const code = problem && typeof problem === "object" && "code" in problem && typeof problem.code === "string" ? problem.code : undefined;
    throw new ApiError(response.status, message, code);
  }
  return response.blob();
}

export async function uploadProfessionalForm<T>(path: string, method: "POST" | "PUT", input: ProfessionalSubmissionInput | ProfessionalAdditionInput): Promise<T> {
  const form = new FormData();
  form.append("manifest", JSON.stringify({
    ...( "professionTitle" in input ? { professionTitle: input.professionTitle } : {}),
    credentials: input.credentials.map(({ evidence, ...credential }) => ({
      ...credential,
      imageCount: evidence.length,
    })),
  }));
  for (const credential of input.credentials) {
    for (const file of credential.evidence) form.append("evidence", file, file.name);
  }

  const token = getToken();
  const response = await profileFetch(`${API_BASE}${path}`, {
    method,
    body: form,
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (response.status === 401) throw new Error("Authentication is required.");
  const data = await response.json();
  if (!response.ok) {
    if (response.status === 429) {
      const retryAfter = (data as { retryAfter?: number })?.retryAfter;
      throw new Error((data as { error?: string })?.error || `You're doing that too fast. Please wait${retryAfter ? ` ${retryAfter}s` : " a moment"} and try again.`);
    }
    throw new Error((data as { error?: string })?.error || `HTTP ${response.status}`);
  }
  return data as T;
}
